import {
    CheckCircle,
    ErrorOutline
} from "@mui/icons-material";
import {
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    alpha,
    styled
} from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import { i18n } from "../../translate/i18n";

// Componentes estilizados
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    margin: theme.spacing(1, 0),
    transition: 'all 0.3s ease',
    '& .MuiOutlinedInput-root': {
        transition: 'all 0.3s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.5),
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        }
    }
}));

const StyledChip = styled(Chip)(({ theme, backgroundColor }) => ({
    margin: theme.spacing(0.5),
    transition: 'all 0.3s ease',
    borderRadius: '5px',
    backgroundColor: alpha(backgroundColor || theme.palette.primary.main, 0.9),
    color: theme.palette.getContrastText(backgroundColor || theme.palette.primary.main),
    fontWeight: 500,
    border: 'none',
    '&:hover': {
        backgroundColor: alpha(backgroundColor || theme.palette.primary.main, 0.7),
        boxShadow: theme.shadows[1],
        transform: 'translateY(-1px)'
    },
    '& .MuiChip-deleteIcon': {
        color: 'inherit',
        opacity: 0.7,
        transition: 'all 0.3s ease',
        '&:hover': {
            opacity: 1,
            color: 'inherit'
        }
    }
}));

const ColorBox = styled(Box)(({ color, theme }) => ({
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: color || theme.palette.primary.main,
    marginRight: theme.spacing(1),
    border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
    transition: 'all 0.3s ease',
    flexShrink: 0
}));

const LoadingWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.background.paper, 0.7),
    border: `1px dashed ${alpha(theme.palette.divider, 0.7)}`,
}));

const QueueSelect = ({ selectedQueueIds, onChange }) => {
    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                setLoading(true);
                setError(false);

                const { data } = await api.get("/queue");
                setQueues(data);
            } catch (err) {
                setError(true);
                ToastError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchQueues();
    }, []);

    // Validação contínua: sempre que queues ou selectedQueueIds mudarem,
    // garante que apenas IDs válidos sejam mantidos
    useEffect(() => {
        if (!loading && queues.length > 0 && selectedQueueIds && selectedQueueIds.length > 0) {
            const validQueueIds = queues.map(q => q.id);
            const filteredIds = selectedQueueIds.filter(id => validQueueIds.includes(id));

            // Se algum ID inválido foi detectado, atualizar automaticamente
            if (filteredIds.length !== selectedQueueIds.length) {
                const removedIds = selectedQueueIds.filter(id => !validQueueIds.includes(id));
                console.warn('QueueSelect: IDs de filas inválidos removidos automaticamente:', removedIds);
                onChange(filteredIds);
            }
        }
    }, [queues, selectedQueueIds, loading]);

    const handleChange = (e) => {
        onChange(e.target.value);
    };

    const getQueueName = (id) => {
        const queue = queues.find((q) => q.id === id);
        return queue ? queue.name : "";
    };

    const getQueueColor = (id) => {
        const queue = queues.find((q) => q.id === id);
        return queue ? queue.color : "#999999";
    };

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={24} color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                    {i18n.t("queueSelect.loading") || "Carregando setores..."}
                </Typography>
            </LoadingWrapper>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'error.light',
                    borderRadius: 1,
                    bgcolor: alpha('#f44336', 0.08)
                }}
            >
                <ErrorOutline color="error" sx={{ mr: 1 }} fontSize="small" />
                <Typography variant="body2" color="error">
                    {i18n.t("queueSelect.error") || "Erro ao carregar setores"}
                </Typography>
            </Box>
        );
    }
    console.info({ selectedQueueIds })
    return (
        <StyledFormControl
            fullWidth
            variant="outlined"
            size="small"
        >
            <InputLabel id="queue-selection-label">
                {i18n.t("ticketsQueueSelect.placeholder")}
            </InputLabel>
            <Select
                labelId="queue-selection-label"
                multiple
                value={selectedQueueIds || []}
                onChange={handleChange}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                label={i18n.t("queueSelect.inputLabel")}
                size="small"
                renderValue={(selected) => (
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        maxHeight: '80px',
                        overflow: 'auto',
                        m: -0.5,
                        p: 0.5
                    }}>
                        {selected?.length > 0 ? (
                            selected.map((id) => (
                                <StyledChip
                                    key={id}
                                    label={getQueueName(id)}
                                    backgroundColor={getQueueColor(id)}
                                    size="small"
                                    icon={<ColorBox color={getQueueColor(id)} />}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', p: 0.5 }}>
                                {i18n.t("queueSelect.noSelection") || "Nenhum setor selecionado"}
                            </Typography>
                        )}
                    </Box>
                )}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 250,
                            transition: 'all 0.3s ease',
                        }
                    },
                    TransitionProps: {
                        timeout: 300
                    }
                }}
            >
                {queues.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="textSecondary">
                            {i18n.t("queueSelect.noQueues") || "Nenhum setor disponível"}
                        </Typography>
                    </MenuItem>
                ) : (
                    queues.map((queue) => (
                        <MenuItem
                            key={queue.id}
                            value={queue.id}
                            sx={{
                                transition: 'all 0.3s ease',
                                borderLeft: '3px solid transparent',
                                '&.Mui-selected': {
                                    borderLeft: `3px solid ${queue.color}`,
                                    backgroundColor: alpha(queue.color, 0.1),
                                },
                                '&.Mui-selected:hover': {
                                    backgroundColor: alpha(queue.color, 0.15),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(queue.color, 0.05),
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <ColorBox color={queue.color} />
                                <Typography variant="body2">{queue.name}</Typography>

                                {selectedQueueIds?.includes(queue.id) && (
                                    <CheckCircle
                                        fontSize="small"
                                        color="primary"
                                        sx={{ ml: 'auto', opacity: 0.7 }}
                                    />
                                )}
                            </Box>
                        </MenuItem>
                    ))
                )}
            </Select>
        </StyledFormControl>
    );
};

export default QueueSelect;